import { Component, Listen, Prop, Query } from '@atomify/core';
import { inviewObserver } from '@utilities/inview';
import EmblaCarousel, { EmblaCarouselType, EmblaOptionsType } from 'embla-carousel';

export interface SliderInterface extends HTMLElement {
    select: (index: number, options?: { jump?: boolean }) => void;
}

const SHOW_FADE_START = 'slider-embla--fade-start';
const SHOW_FADE_END = 'slider-embla--fade-end';

@Component({
    tag: 'slider-embla',
})
export class Slider extends HTMLElement {
    @Prop() centered: boolean = false;
    @Prop() sliderOptions: string | null = null;
    @Prop() controlMode: string | null = null;
    @Prop({ type: 'Boolean' }) showFade: boolean = false;
    @Prop() onSelect: Function | null = null;

    @Query('[js-hook-slider]') slider: HTMLElement;
    @Query('[js-hook-slider-control-previous]') sliderPrevious: HTMLElement | null;
    @Query('[js-hook-slider-control-next]') sliderNext: HTMLElement | null;

    embla: EmblaCarouselType | undefined;
    slideNodes: HTMLElement[];
    currentSlideIndex: number;
    isInView: boolean = false;

    @Listen('click', { target: 'sliderNext' })
    nextSlide() {
        if (this.controlMode === 'start-end') {
            this.embla?.scrollTo(this.slideNodes.length - 1);
        } else {
            this.embla?.scrollNext();
        }
    }

    @Listen('click', { target: 'sliderPrevious' })
    previousSlide() {
        if (this.controlMode === 'start-end') {
            this.embla?.scrollTo(0);
        } else {
            this.embla?.scrollPrev();
        }
    }

    setSelectedState() {
        if (!this.embla) return;

        if (!this.sliderNext || !this.sliderPrevious) return;

        if (!this.embla.canScrollNext()) {
            this.sliderNext.setAttribute('disabled', 'disabled');
        } else {
            this.sliderNext.removeAttribute('disabled');
        }

        if (!this.embla.canScrollPrev()) {
            this.sliderPrevious.setAttribute('disabled', 'disabled');
        } else {
            this.sliderPrevious.removeAttribute('disabled');
        }
    }

    setFade() {
        if (!this.embla || !this.slideNodes) return;

        const slidesNotInView = this.embla.slidesNotInView();
        const currScrollSnap = this.embla.selectedScrollSnap();
        const lastSlideIndex = this.slideNodes.length - 1;

        if (slidesNotInView.includes(0) && currScrollSnap > 0) {
            this.classList.add(SHOW_FADE_START);
        } else {
            this.classList.remove(SHOW_FADE_START);
        }

        if (slidesNotInView.includes(lastSlideIndex) && currScrollSnap < lastSlideIndex) {
            this.classList.add(SHOW_FADE_END);
        } else {
            this.classList.remove(SHOW_FADE_END);
        }
    }

    public select(index: number, options?: { jump?: boolean }) {
        if (this.currentSlideIndex !== index) {
            this.currentSlideIndex = index;
            this.embla?.scrollTo(index, options?.jump);
        }
    }

    checkKey(e: KeyboardEvent) {
        if (this.isInView) {
            if (e.key == 'ArrowLeft' || e.key == 'ArrowUp') {
                this.previousSlide();
            } else if (e.key == 'ArrowRight' || e.key == 'ArrowDown') {
                this.nextSlide();
            }
        }
    }

    initSlider() {
        const emblaNode = this.slider;
        const emblaOptions: EmblaOptionsType = {
            align: 'start',
            containScroll: 'trimSnaps',
            inViewThreshold: 1,
            ...(this.sliderOptions && (JSON.parse(this.sliderOptions) as EmblaOptionsType)),
        };

        if (this.centered) {
            emblaOptions.align = 'center';
            emblaOptions.containScroll = 'keepSnaps';
        }

        this.embla = EmblaCarousel(emblaNode, emblaOptions);

        this.slideNodes = this.embla?.slideNodes();

        if (this.embla) {
            this.embla.on('select', () => {
                this.setSelectedState();
                this.onSelect && this.onSelect(this.embla?.selectedScrollSnap());
            });

            if (this.showFade) {
                this.embla.on('settle', () => {
                    this.setFade();
                });
            }
        }

        this.slideNodes.forEach((slideNode, index) => {
            slideNode.addEventListener('click', () => {
                this.select(index);
            });
        });
    }

    destroySlider() {
        if (this.embla) {
            this.embla.destroy();
            this.embla = undefined;
        }
    }

    setupSlider() {
        if (!this.embla) {
            this.initSlider();
        } else {
            this.destroySlider();
        }
    }

    componentDidLoad() {
        inviewObserver.observe(this.slider, (inView) => {
            this.isInView = inView;
        });

        window.addEventListener('keydown', (e: KeyboardEvent) => {
            if (this.isInView) this.checkKey(e);
        });

        this.setupSlider();
        this.setSelectedState();
        this.showFade && this.setFade();
    }
}
